/**
 * segmentNodeStyles.ts - Styles for the SegmentNode component
 *
 * Segment nodes are rectangular cards that serve as entry points for conditional branches.
 * They contain tag filtering configuration and visual indicators for the segment type.
 */

import { StyleSheet } from 'aphrodite';
import {
	brandPrimary,
	brandSecondary,
	titles,
	darkGrayFontColor,
	inputBorderColor,
	white,
	background,
	nodeBorderBlue,
	charmGray,
} from '../../../styles/colors';
import { SEGMENT_NODE, SPACING, FONT_SIZE, BORDER_RADIUS } from '../utils/layoutConstants';

// Alias for consistency with component code
const subtitles = darkGrayFontColor;
const inputBorder = inputBorderColor;
import { shadows, transitions } from '../styles/baseStyles';

export const styleSheet = StyleSheet.create({
	// Container
	container: {
		width: SEGMENT_NODE.WIDTH,
		minHeight: 'auto', // Allow content to determine height for compact layout
		backgroundColor: white,
		borderRadius: BORDER_RADIUS.LARGE,
		border: `2px solid ${inputBorder}`,
		boxShadow: shadows.card,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		transition: transitions.card,
		overflow: 'hidden',
	},
	containerSelected: {
		boxShadow: `0 0 0 2px ${nodeBorderBlue}, ${shadows.card}`,
		borderColor: nodeBorderBlue,
	},
	containerPath: {
		boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.18), ${shadows.card}`,
	},
	containerTarget: {
		boxShadow: `0 0 0 4px rgba(220, 38, 38, 0.18), ${shadows.card}`,
	},
	containerDefault: {
		borderColor: charmGray,
	},

	// Header
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: `${SPACING.MD}px ${SPACING.LG}px`,
		borderBottom: `1px solid ${inputBorder}`,
		backgroundColor: background,
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.SM,
		minWidth: 0,
		flex: 1,
	},
	colorDot: {
		width: 20,
		height: 20,
		borderRadius: '50%',
		flexShrink: 0,
	},
	segmentTitle: {
		fontSize: FONT_SIZE.MD,
		fontWeight: 600,
		color: titles,
		margin: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: 0,
		flex: 1,
	},
	segmentNamePlaceholder: {
		fontWeight: 400,
		color: charmGray,
		fontStyle: 'italic',
	},
	editNameButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		flexShrink: 0,
		marginLeft: SPACING.XS,
	},
	headerActions: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.XS,
	},
	actionButton: {
		width: 28,
		height: 28,
		borderRadius: BORDER_RADIUS.SMALL,
		border: 'none',
		backgroundColor: 'transparent',
		color: subtitles,
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: transitions.all,
		':hover': {
			backgroundColor: inputBorder,
			color: titles,
		},
	},

	// Content area
	content: {
		padding: SPACING.LG,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACING.MD,
		flex: 1,
	},

	// Description text
	description: {
		fontSize: FONT_SIZE.SM,
		color: subtitles,
		margin: 0,
	},
	validationMessage: {
		fontSize: FONT_SIZE.SM,
		color: '#DC2626',
		fontWeight: 500,
		margin: 0,
	},
	inlineHint: {
		fontSize: FONT_SIZE.XS,
		color: subtitles,
		margin: `${SPACING.XS}px 0 0 52px`,
	},

	// Tag sections
	tagSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACING.SM,
	},
	tagSectionLabel: {
		fontSize: FONT_SIZE.SM,
		fontWeight: 500,
		color: titles,
		margin: 0,
	},
	tagSectionSublabel: {
		fontSize: FONT_SIZE.XS,
		color: subtitles,
		fontWeight: 400,
	},
	tagChipsContainer: {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: SPACING.XS,
		alignItems: 'center',
	},
	tagChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: SPACING.XS,
		padding: `${SPACING.XS}px ${SPACING.SM}px`,
		backgroundColor: '#E0F2FE', // Light blue background
		borderRadius: BORDER_RADIUS.SMALL,
		fontSize: FONT_SIZE.XS,
		color: '#0369A1', // Blue text
		fontWeight: 500,
	},
	tagChipRemove: {
		width: 14,
		height: 14,
		borderRadius: '50%',
		border: 'none',
		backgroundColor: 'transparent',
		color: '#0369A1',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		':hover': {
			backgroundColor: '#BAE6FD',
		},
	},
	addTagButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: SPACING.XS,
		padding: `${SPACING.XS}px ${SPACING.SM}px`,
		backgroundColor: 'transparent',
		border: `1px dashed ${brandPrimary}`,
		borderRadius: BORDER_RADIUS.SMALL,
		fontSize: FONT_SIZE.XS,
		color: brandPrimary,
		fontWeight: 500,
		cursor: 'pointer',
		transition: transitions.all,
		':hover': {
			backgroundColor: '#EEF2FF',
			borderStyle: 'solid',
		},
	},

	// Required tags checkbox section
	requiredTagsSection: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: SPACING.SM,
		paddingTop: SPACING.SM,
		borderTop: `1px solid ${inputBorder}`,
	},
	checkbox: {
		width: 16,
		height: 16,
		marginTop: 2,
		accentColor: brandPrimary,
		cursor: 'pointer',
	},
	requiredTagsLabel: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACING.XS,
		flex: 1,
	},
	requiredTagsText: {
		fontSize: FONT_SIZE.SM,
		color: titles,
	},
	requiredTagsDropdown: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: SPACING.XS,
		padding: `${SPACING.XS}px ${SPACING.SM}px`,
		backgroundColor: white,
		border: `1px solid ${inputBorder}`,
		borderRadius: BORDER_RADIUS.SMALL,
		fontSize: FONT_SIZE.XS,
		color: titles,
		cursor: 'pointer',
	},

	// Filter sections (Included tags / Narrow the list)
	filterSection: {
		marginTop: SPACING.MD,
	},
	filterSectionDisabled: {
		opacity: 0.72,
	},
	filterSectionHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.SM,
		marginBottom: SPACING.SM,
	},
	filterSectionTitle: {
		fontSize: FONT_SIZE.SM,
		fontWeight: 500,
		color: titles,
		margin: 0,
	},
	filterSectionSubtext: {
		fontWeight: 400,
		color: subtitles,
	},
	boldText: {
		fontWeight: 600,
	},
	tagListContainer: {
		marginLeft: 52, // Align with text after icon (icon width + gap)
		marginBottom: SPACING.SM,
	},
	indentedTagList: {
		marginLeft: 24,
		marginBottom: SPACING.MD,
	},
	narrowContent: {
		marginLeft: 52, // Align with text after icon
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.SM,
		marginBottom: SPACING.SM,
		cursor: 'pointer',
	},
	filterCheckbox: {
		marginBottom: SPACING.SM,
	},
	checkboxText: {
		fontSize: FONT_SIZE.SM,
		color: titles,
	},
	tagChipNot: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: SPACING.XS,
		padding: `${SPACING.XS}px ${SPACING.SM}px`,
		backgroundColor: '#FEE2E2', // Light red background for NOT tags
		borderRadius: BORDER_RADIUS.SMALL,
		fontSize: FONT_SIZE.XS,
		color: '#DC2626', // Red text
		fontWeight: 500,
	},

	// Default segment specific styles
	defaultDescription: {
		fontSize: FONT_SIZE.SM,
		color: subtitles,
		margin: 0,
		fontStyle: 'italic',
	},

	// React Flow handles
	handle: {
		width: 12,
		height: 12,
		backgroundColor: brandSecondary,
		border: `2px solid ${white}`,
		borderRadius: '50%',
	},
	handleHidden: {
		opacity: 0,
		pointerEvents: 'none' as const,
	},

	// Compact view (collapsed)
	compactContainer: {
		width: SEGMENT_NODE.WIDTH,
		height: SEGMENT_NODE.COLLAPSED_HEIGHT,
		backgroundColor: white,
		borderRadius: BORDER_RADIUS.LARGE,
		border: `2px solid ${inputBorder}`,
		boxShadow: shadows.card,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		transition: transitions.card,
		cursor: 'pointer',
	},
	compactContent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: SPACING.LG,
		flex: 1,
	},
	compactLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACING.SM,
		minWidth: 0,
		flex: 1,
	},
	compactSummary: {
		fontSize: FONT_SIZE.XS,
		color: subtitles,
		marginTop: SPACING.XS,
	},

	// Add filter button container
	addFilterButtonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: SPACING.SM,
	},
	addFilterButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: SPACING.XS,
		padding: `${SPACING.XS}px ${SPACING.SM}px`,
		backgroundColor: 'transparent',
		border: 'none',
		fontSize: FONT_SIZE.SM,
		color: brandPrimary,
		fontWeight: 500,
		cursor: 'pointer',
		transition: transitions.all,
		':hover': {
			opacity: 0.8,
		},
	},
});

// Backwards compatible alias
export const segmentNodeStyles = styleSheet;
